import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getCurrentUserToken } from '../../utils/cookieUtils';
import { Host } from '../Apis';

// Base query with authentication
const baseQuery = fetchBaseQuery({
  baseUrl: Host,
  prepareHeaders: (headers, { getState }) => {
    // Get token from cookies
    const token = getCurrentUserToken();
    
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    
    // Don't set content-type here - let RTK Query handle it automatically
    // It will set the correct content-type based on the body type
    headers.set('accept', 'application/json');
    
    return headers;
  },
});

// Base query with reauth logic
const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);
  
  // Handle "No available exams" response as success with empty data
  if (result.data && (result.data.message === "No available exams" || result.data.message === "No exams today")) {
    return {
      ...result,
      data: []
    };
  }
  
  // Handle 404 errors for examspage endpoint specifically
  if (result.error && result.error.status === 404 && args.url && (args.url.includes('/students/examspage') || args.url.includes('examspage'))) {
    // Check if it's a "no exams" 404 error
    if (result.error.data && (result.error.data.message === "No available exams" || result.error.data.message === "No exams today")) {
      return {
        data: [],
        meta: { request: args, response: result }
      };
    }
  }
  
  // Skip 401 handling for auth API endpoints
  const isAuthEndpoint = args.url && (
    args.url.includes('/api/v1/students/login') ||
    args.url.includes('/api/v1/adminlogin') ||
    args.url.includes('/api/v1/admin/stupass')
  );
  
  if (result.error && result.error.status === 401 && !isAuthEndpoint) {
    // Handle unauthorized access - clear cookies and redirect
    document.cookie = 'auth_token=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;SameSite=Strict';
    document.cookie = 'auth_tokena=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;SameSite=Strict';
    window.location.href = '/login';
  }
  
  return result;
};

// Main API slice
export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  /*
   | كل نوع وسم يستعمله أي slice في providesTags أو invalidatesTags يجب أن
   | يكون هنا. كانت القائمة تسجّل 17 نوعًا بينما الـ slices تستعمل 18 نوعًا
   | آخر غير مسجّل.
   |
   | تصحيح لما ورد في نسخة سابقة من الأوديت: الإبطال **يعمل** رغم ذلك.
   | راجعنا مصدر @reduxjs/toolkit المثبَّت: assertTagType تُرجع الوسم كما هو
   | ولا تُسقطه، والمساران يستعملانه. الأثر الفعلي console.error مع كل
   | استدعاء في التطوير، وقائمة تصف واقعًا غير صحيح — لا كاش معطوب.
   |
   | وأُبقي على ExamSchedule و User رغم أن أحدًا لا يستعملهما اليوم: وجود
   | اسم زائد لا يكلّف شيئًا، وحذف اسم قد يستعمله slice لم نره يكسر شيئًا
   | صامتًا. الاتجاه هنا نحو الشمول لا التقليم.
   */
  tagTypes: [
    // موجودة أصلًا
    'Exam',
    'Student',
    'Question',
    'Group',
    'Subject',
    'StudyYear',
    'Lecturer',
    'Result',
    'Followup',
    'ExamSchedule',
    'User',
    'Auth',
    'Answer',
    'ExamStats',
    'LecturerProfile',
    'LecturerSubjects',
    'LecturerStats',

    // كانت مستعملة في الـ slices وغير مسجّلة
    'Dashboard',
    'ExamAvailability',
    'ExamProgress',
    'FollowupNote',
    'GroupStats',
    'GroupStudent',
    'ResultStats',
    'StudentExam',
    'StudentExamHistory',
    'StudentExamQuestions',
    'StudentExamSchedule',
    'StudentExamStats',
    'StudentExams',
    'StudentPastExams',
    'StudentPersonalInfo',
    'StudentProfile',
    'StudentResults',
    'StudentStats',
  ],
  endpoints: (builder) => ({}),

});

export default apiSlice;
